// frontend/src/pages/ForaKitPage.tsx

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { AgGridReact } from '@ag-grid-community/react';
import { ColDef, ModuleRegistry, ICellRendererParams, ValueFormatterParams, CellValueChangedEvent, ITooltipParams } from '@ag-grid-community/core';
import { ClientSideRowModelModule } from '@ag-grid-community/client-side-row-model';
import { MenuModule } from '@ag-grid-enterprise/menu';
import { SetFilterModule } from '@ag-grid-enterprise/set-filter';
import { RangeSelectionModule } from '@ag-grid-enterprise/range-selection';
import { ToastContainer, toast } from 'react-toastify';

// Estilos
import '@ag-grid-community/styles/ag-grid.css';
import '@ag-grid-community/styles/ag-theme-quartz.css';
import '../styles/ForaKitPage.css'; // Nosso CSS customizado
import 'react-toastify/dist/ReactToastify.css'; // Estilos para as notificações

// Hooks e Utilitários
import { useAuth } from '../hooks/useAuth';
import { authFetch } from '../utils/authFetch';

// Registro de Módulos do AG Grid
ModuleRegistry.registerModules([
  ClientSideRowModelModule,
  MenuModule,
  SetFilterModule,
  RangeSelectionModule
]);

// --- Componente para Renderizar os Badges Coloridos ---
const DesembaracoCellRenderer = (props: ICellRendererParams) => {
  if (!props.value) {
    return <span>{props.value}</span>;
  }
  
  const value = String(props.value).toUpperCase();
  let className = 'status-badge';
  let applyBadge = false;

  if (value.includes('VERMELHO')) {
    className += ' red';
    applyBadge = true;
  } else if (value.includes('AMARELO')) {
    className += ' yellow';
    applyBadge = true;
  } else if (value.includes('CINZA')) {
    className += ' gray';
    applyBadge = true;
  } else if (value.includes('VERDE')) {
    className += ' green';
    applyBadge = true;
  }
  
  if (applyBadge) {
    return <span className={className}>{props.value}</span>;
  }
  
  return <span>{props.value}</span>;
};


// Função para formatar datas no padrão dd/mm/YYYY
const dateFormatter = (params: ValueFormatterParams): string => {
  if (!params.value) return '';
  try {
    const date = new Date(params.value);
    if (isNaN(date.getTime())) {
      return String(params.value);
    }
    return date.toLocaleDateString('pt-BR', { timeZone: 'UTC' });
  } catch (e) {
    return String(params.value);
  }
};

interface IForaKitRow {
  id: number;
}

export const ForaKitPage = () => {
  const [rowData, setRowData] = useState<IForaKitRow[]>([]);
  const { role } = useAuth();
  const isMasterUser = role === 'master';

  const columnDefs: ColDef[] = useMemo(() => [
    { field: 'planejador', headerName: 'PLANEJADOR', filter: 'agSetColumnFilter', minWidth: 150 },
    { field: 'linha_de_produto', headerName: 'LINHA DE PRODUTO', filter: 'agSetColumnFilter', minWidth: 180 },
    { field: 'processo', headerName: 'PROCESSO', filter: 'agSetColumnFilter', minWidth: 150 },
    { field: 'supplier', headerName: 'SUPPLIER', filter: 'agSetColumnFilter', minWidth: 250 },
    { field: 'po_sap', headerName: 'PO SAP', filter: 'agTextColumnFilter', minWidth: 150 },
    { field: 'item', headerName: 'ITEM', filter: 'agTextColumnFilter', minWidth: 120 },
    { field: 'quantidade', headerName: 'QUANTIDADE', filter: 'agNumberColumnFilter', minWidth: 130 },
    { field: 'bl_awb', headerName: 'BL/AWB', filter: 'agTextColumnFilter', minWidth: 150 },
    { field: 'armador_cia', headerName: 'ARMADOR/CIA', filter: 'agSetColumnFilter', minWidth: 180 },
    { field: 'cntr_mawb', headerName: '#CNTR / MAWB', filter: 'agTextColumnFilter', minWidth: 180 },
    { field: 'invoice', headerName: 'INVOICE', filter: 'agTextColumnFilter', minWidth: 150 },
    { field: 'despachante', headerName: 'DESPACHANTE', filter: 'agSetColumnFilter', minWidth: 150 },
    { field: 'status', headerName: 'STATUS', filter: 'agSetColumnFilter', minWidth: 200 },
    { field: 'desembaraco', headerName: 'DESEMBARAÇO', filter: 'agSetColumnFilter', minWidth: 180, cellRenderer: DesembaracoCellRenderer },
    { field: 'etd_prometido', headerName: 'ETD PROMETIDO', filter: 'agDateColumnFilter', valueFormatter: dateFormatter, minWidth: 150 },
    { field: 'etd_final', headerName: 'ETD FINAL', filter: 'agDateColumnFilter', valueFormatter: dateFormatter, minWidth: 150 },
    { field: 'navio_de_transbordo_mao', headerName: 'NAVIO DE TRANSBORDO (MAO)', filter: 'agTextColumnFilter', minWidth: 250 },
    { field: 'eta_mao', headerName: 'ETA MAO', filter: 'agDateColumnFilter', valueFormatter: dateFormatter, minWidth: 150 },
    { field: 'entrega', headerName: 'ENTREGA', filter: 'agDateColumnFilter', valueFormatter: dateFormatter, minWidth: 150 },
    { field: 'tt', headerName: 'TT', filter: 'agNumberColumnFilter', minWidth: 100 },
    { field: 'delivery_days', headerName: 'DELIVERY DAYS', filter: 'agNumberColumnFilter', minWidth: 150 },
    { field: 'delivery_status', headerName: 'DELIVERY STATUS', filter: 'agSetColumnFilter', minWidth: 180 },
    { field: 'cadastro', headerName: 'CADASTRO', filter: 'agSetColumnFilter', minWidth: 150 },
    { field: 'obs', headerName: 'OBS', filter: 'agTextColumnFilter', minWidth: 300 },
  ], []);

  // Definição padrão para todas as colunas (apenas centralização via classes)
  const defaultColDef = useMemo<ColDef>(() => {
    return {
      sortable: true,
      resizable: true,
      floatingFilter: true,
      editable: isMasterUser,
      tooltipValueGetter: (params: ITooltipParams) => params.value,

      // ÚNICA MUDANÇA: classes para centralizar (tratadas no CSS)
      headerClass: 'header-center',
      cellClass: 'cell-center',
    };
  }, [isMasterUser]);

  // Busca os dados da API ao carregar a página
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await authFetch('/api/forakit/data');
        if (!response.ok) throw new Error('Falha ao buscar dados');
        const data = await response.json();
        setRowData(data);
      } catch (error) {
        console.error(error);
        toast.error('Não foi possível carregar os dados da tabela.');
      }
    };
    fetchData();
  }, []);

  // Função para lidar com a alteração do valor de uma célula
  const handleCellValueChanged = useCallback(async (event: CellValueChangedEvent) => {
    const { data, colDef, newValue, oldValue } = event;
    const field = colDef.field;
    const rowId = data.id;

    if (!field || newValue === oldValue) {
      return;
    }

    const updateData = { 
      field: field,
      value: newValue 
    };
    
    const toastId = toast.loading("Salvando alteração...");

    try {
      const response = await authFetch(`/api/forakit/update/${rowId}`, {
        method: 'PUT',
        body: JSON.stringify(updateData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Falha ao salvar a alteração.');
      }

      toast.update(toastId, { render: "Salvo com sucesso!", type: "success", isLoading: false, autoClose: 3000 });
    } catch (error: any) {
      console.error("Erro ao salvar:", error);
      toast.update(toastId, { render: `Erro: ${error.message}`, type: "error", isLoading: false, autoClose: 5000 });
      
      // Desfaz a alteração na UI em caso de erro
      event.api.applyTransaction({ update: [{ ...data, [field]: oldValue }] });
    }
  }, []);

  return (
    <>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
      />
      <div className="ag-theme-quartz" style={{ height: 'calc(100vh - 130px)', width: '100%' }}>
        <AgGridReact
          rowData={rowData}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          onCellValueChanged={handleCellValueChanged}
          animateRows={true}
          enableCellTextSelection={true}
          enableRangeSelection={true}
        />
      </div>
    </>
  );
};